<template>
  <v-dialog
    v-model="dateTimePickerOpen"
    max-width="400"
  >
    <template #activator="{ props: dateTimePickerActivatorProps }">
      <AppTextField
        v-bind="dateTimePickerActivatorProps"
        class="picker-control"
        append-inner-icon="mdi-calendar-blank-outline"
        :density="density"
        :disabled="disabled"
        :hide-details="hideDetails"
        :label="label"
        :model-value="formattedDateTime"
        :name="name"
        :placeholder="placeholder"
        :required="required"
        readonly
        :width="width"
      />
    </template>

    <v-card
      class="dialog-card date-time-field__dialog"
      rounded="xl"
    >
      <header class="date-time-field__header">
        <h3 class="dialog-title">Odaberi datum i vrijeme</h3>
        <v-btn
          aria-label="Zatvori odabir datuma i vremena"
          class="button button--icon dialog-close"
          icon="mdi-close"
          size="x-small"
          variant="text"
          @click="dateTimePickerOpen = false"
        />
      </header>

      <div class="grid-stack">
        <v-date-picker
          color="primary"
          hide-header
          :model-value="selectedDate"
          width="100%"
          @update:model-value="selectedDate = $event"
        />
        <v-time-picker
          color="primary"
          format="24hr"
          hide-header
          :model-value="selectedTime"
          width="100%"
          @update:model-value="selectedTime = $event"
        />
      </div>

      <footer class="date-time-field__actions">
        <v-btn
          class="button button--secondary button--small"
          variant="outlined"
          @click="dateTimePickerOpen = false"
        >
          Odustani
        </v-btn>
        <v-btn
          class="button button--primary button--small"
          :disabled="!selectedDate || !selectedTime"
          variant="flat"
          @click="saveDateTime"
        >
          Potvrdi
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

import { formatDate } from '../helpers'
import AppTextField from './AppTextField.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  label: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  width: {
    type: [String, Number],
    default: '100%',
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  hideDetails: {
    type: [Boolean, String],
    default: 'auto',
  },

  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value),
  },
})

const emit = defineEmits(['update:modelValue'])
const dateTimePickerOpen = ref(false)
const selectedDate = ref(null)
const selectedTime = ref('')

const formattedDateTime = computed(() => {
  if (!props.modelValue) {
    return ''
  }

  const [date, time] = props.modelValue.split('T')

  return `${formatDate(date)}, ${time}`
})

watch(dateTimePickerOpen, (isOpen) => {
  if (!isOpen) {
    return
  }

  const [date, time = ''] = props.modelValue.split('T')
  const [year, month, day] = date.split('-').map(Number)

  selectedDate.value = props.modelValue ? new Date(year, month - 1, day) : new Date()
  selectedTime.value = time
})

function saveDateTime () {
  const month = String(selectedDate.value.getMonth() + 1).padStart(2, '0')
  const day = String(selectedDate.value.getDate()).padStart(2, '0')

  emit(
    'update:modelValue',
    `${selectedDate.value.getFullYear()}-${month}-${day}T${selectedTime.value}`
  )
  dateTimePickerOpen.value = false
}
</script>

<style
  scoped
  lang="scss"
>
.date-time-field {
  &__dialog {
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
  }

  &__header,
  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
  }

  &__header {
    justify-content: space-between;
  }

  &__actions {
    justify-content: flex-end;
    border-top: 1px solid var(--color-border);
  }
}
</style>
